import type {GridColDef, GridRowModel} from "@mui/x-data-grid-premium";
import {CurrencyComponent} from "~/components/currency-component/CurrencyComponent";
import {EMPTY_CELL, formatDate} from "~/components/data-grid/cells/gridCellFormat";
import {toDate} from "~/stores/config/DatabagConfigStore";

const BLANK_LABEL = '—';

/**
 * Columns for the Transactions tab's ledger: Part name (`p_name`) first, then the design's own order
 * (`suppliers360.jsx::SxTransactionsTab`: Date · Description · Category · PO number · Amount).
 *
 * Field names are flat and resolved server-side through the request's `related_data`
 * (`p_name` → `part__p_name`, `p_ddate_1` → `part__p_ddate_1`, `l1` → `cat__l1`), so every column filters and sorts on the
 * server. The default operators MUI offers per column type are all ones the endpoint supports
 * (`contains`/`equals`/`isAnyOf`/`isEmpty` for text, `is`/`after`/`before` for dates,
 * comparisons + `between` for numbers), so none are filtered down here.
 *
 * Departures from the design, about what the data holds:
 * - **No PO number column**: `Part` has no purchase-order field; Part name identifies the row instead.
 * - **No Status column**: nothing on `MatPartReviewRow`/`Part` carries a transaction status, so the
 *   design's status pill has no backing field.
 *
 * "Supplier" (`s_name`) is added instead: the grid is cluster-wide (every `ClusterMember`'s own
 * `s_id`), so it's what tells rows of one clustered raw supplier from another's.
 */
export function getSupplierTransactionColumns(): GridColDef<GridRowModel>[] {
    return [
        {
            field: 'p_name',
            headerName: 'Part name',
            flex: 1,
            minWidth: 200,
            renderCell: ({value}) => value || BLANK_LABEL,
        },
        {
            field: 'p_ddate_1',
            headerName: 'Date',
            type: 'date',
            width: 110,
            // The API sends an ISO string; a date column needs a real Date to filter and format.
            valueGetter: toDate,
            // Shown as "31 Dec 2025" like the rest of Supplier 360, not the grid's locale default.
            valueFormatter: (value: Date | null) => value ? formatDate(value.toISOString()) : EMPTY_CELL,
        },
        {
            field: 'p_description',
            headerName: 'Description',
            flex: 1.5,
            minWidth: 220,
            renderCell: ({value}) => value || BLANK_LABEL,
        },
        {
            field: 'l1',
            headerName: 'Category',
            width: 180,
            renderCell: ({value}) => value || BLANK_LABEL,
        },
        {
            field: 's_name',
            headerName: 'Supplier',
            width: 180,
            renderCell: ({value}) => value || BLANK_LABEL,
        },
        {
            field: 'p_spend',
            headerName: 'Amount',
            type: 'number',
            headerAlign: 'left',
            width: 120,
            // A DRF decimal can arrive as a string, so coerce before the currency cell formats it.
            valueGetter: value => Number(value ?? 0),
            renderCell: ({value}) => <CurrencyComponent v={value} nDigits={3}/>,
        },
    ];
}
